import { RowState } from './dataset.js';
import { formatNumber } from './format.js';
import { EXTRA, NIR, isUntouchedRow, summarizeValues, MAX_SCROLL_PX, AGG_LABEL } from './grid-util.js';
export class GridSelect {
    focusCell(row, col, edit = false) {
        let vi = -1;
        for (let i = 0; i < this.disp.length; i++)
            if (this.disp[i] === row) {
                vi = i;
                break;
            }
        if (vi < 0)
            return;
        const ci = col ? this.cols.findIndex(c => c.id === col) : this.focus.ci;
        this.setFocus(vi, ci < 0 ? this.focus.ci : ci, false);
        this.ime.focus({ preventScroll: true });
        if (edit)
            this.beginEdit('full');
    }
    /** 그리드에 키보드 포커스 */
    focusGrid() { this.ime.focus({ preventScroll: true }); }
    /** 포커스 셀을 화면 낭독기에 알림: "행 n, 컬럼명, 값" (+ 상태·오류) */
    /** @internal */ announce() {
        if (this.liveTimer)
            clearTimeout(this.liveTimer);
        this.liveTimer = setTimeout(() => {
            this.liveTimer = null;
            const vi = this.focus.vi, item = vi === NIR ? this.nirRow : this.disp[vi], c = this.cols[this.focus.ci];
            if (item == null || !c)
                return;
            let msg;
            if (vi === NIR)
                msg = `${this.t('새 항목 행')}, ${c.def.header ?? c.id}, ${(item >= 0 && !c.special ? this.cellText(item, c.id) : '') || this.t('빈 값')}`;
            else if (item < 0 && item > EXTRA) {
                const g = this.groups[-item - 1];
                msg = `그룹 ${g.label}, ${g.count ?? g.rows.length}건, ${g.collapsed ? this.t('접힘') : this.t('펼침')}`;
            }
            else if (item >= 0) {
                const no = (this.rowNo ? this.rowNo[vi] : vi) + 1;
                const text = c.special === 'check' ? (this.checked.has(item) ? this.t('선택됨') : this.t('선택 안 됨')) : c.special === 'rownum' ? String(no) : this.cellText(item, c.id);
                const st = this.ds.rowState(item), err = this.errors.get(item + ':' + c.id);
                msg = `행 ${no}, ${c.def.header ?? c.id}, ${text || this.t('빈 값')}${st === RowState.INSERT ? ', 새 행' : st === RowState.UPDATE ? ', 수정됨' : ''}${err ? ', 오류: ' + err : ''}`;
            }
            else
                return;
            if (this.live.textContent !== msg)
                this.live.textContent = msg;
        }, 120);
    }
    /** 시트 보호: 잠긴 셀(컬럼 locked 기본 true, setCellLocked 로 셀별 지정)은 편집·붙여넣기·채우기·지우기 거부. 행 추가·삭제도 막음(허용 옵션). 화면 제어일 뿐 — 서버 검증은 따로 */
    protect(opts = {}) {
        this.cancelEdit();
        this.protectOn = { password: opts.password ?? null, allowInsertRows: !!opts.allowInsertRows, allowDeleteRows: !!opts.allowDeleteRows };
        this.root.classList.add('og-protected');
        this.bump();
        this.rowStateChanged();
    }
    /** 보호 해제. 암호가 틀리면 false */
    unprotect(password) {
        if (!this.protectOn)
            return true;
        if (this.protectOn.password != null && password !== this.protectOn.password)
            return false;
        this.protectOn = null;
        this.root.classList.remove('og-protected');
        this.bump();
        this.rowStateChanged();
        return true;
    }
    isProtected() { return !!this.protectOn; }
    /** 셀 잠금 지정 (null 이면 컬럼 기본값으로) */
    setCellLocked(row, col, locked) { const k = row + ':' + col; if (locked == null)
        this.cellLock.delete(k);
    else
        this.cellLock.set(k, locked); this.bump(); this.rowStateChanged(); }
    isCellLocked(row, col) {
        const x = this.cellLock.get(row + ':' + col);
        if (x != null)
            return x;
        const l = this.opts.columns.find(d => d.id === col)?.locked;
        return typeof l === 'function' ? l(row, this.ds) : l !== false;
    }
    /** @internal */ protectBlocks(what) {
        const p = this.protectOn;
        if (!p || (what === 'insert' ? p.allowInsertRows : p.allowDeleteRows))
            return false;
        this.showTip(this.t(what === 'insert' ? this.t('보호된 시트입니다 — 행을 추가할 수 없습니다') : this.t('보호된 시트입니다 — 행을 삭제할 수 없습니다')));
        setTimeout(() => this.hideTip(), 2000);
        return true;
    }
    // ═══════════════════════ 포커스·선택 ═══════════════════════
    /** @internal */ range() {
        return { r0: Math.min(this.anchor.vi, this.focus.vi), r1: Math.max(this.anchor.vi, this.focus.vi), c0: Math.min(this.anchor.ci, this.focus.ci), c1: Math.max(this.anchor.ci, this.focus.ci) };
    }
    /** @internal */ firstDataCol() { const i = this.cols.findIndex(c => !c.special); return i < 0 ? 0 : i; }
    /** @internal */ setFocus(vi, ci, extend) {
        if (vi === NIR) {
            if (this.nirOpt())
                this.focusNir(ci);
            return;
        }
        // 새 항목 행을 떠남: 고친 게 있으면 검증 후 목록에 붙임(실패하면 머묾), 없으면 버림
        if (this.focus.vi === NIR && !this.leaveNewItemRow())
            return;
        if (this.focus.vi === NIR && !this.disp.length)
            return;
        // 펼친 편집 폼(inlineForm)이 있는 행을 떠나면 폼을 확정 (검증 실패면 머묾)
        if (this.form && this.form.mode === 'inline' && this.disp[Math.max(0, Math.min(this.disp.length - 1, vi))] !== this.form.row && !this.hideEditForm(true))
            return;
        vi = Math.max(0, Math.min(this.disp.length - 1, vi));
        if (this.disp[vi] <= EXTRA) { // 추가 슬롯은 건너뜀 (진행 방향, 끝이면 반대로)
            const d = vi >= this.focus.vi ? 1 : -1;
            let j = vi;
            while (j >= 0 && j < this.disp.length && this.disp[j] <= EXTRA)
                j += d;
            if (j < 0 || j >= this.disp.length) {
                j = vi;
                while (j >= 0 && j < this.disp.length && this.disp[j] <= EXTRA)
                    j -= d;
            }
            vi = Math.max(0, Math.min(this.disp.length - 1, j));
        }
        ci = Math.max(0, Math.min(this.cols.length - 1, ci));
        // 행이 바뀌기 전 확인 (linkGrids 의 미저장 확인 등). false 면 이동하지 않음
        const nr = this.disp[vi];
        const pr = this.lastFocusRow >= 0 ? this.lastFocusRow : this.disp[this.focus.vi] ?? -1;
        // 셀 포커스 옮기기 전 (onBeforeCellFocus): false 면 그대로
        if ((this.opts.onBeforeCellFocus || this.beforeCellFocusListeners.size) && (vi !== this.focus.vi || ci !== this.focus.ci)) {
            const cur = this.disp[this.focus.vi];
            if (!this.beforeCellFocus(nr ?? -1, this.cols[ci]?.id ?? '', cur == null ? -1 : cur, this.cols[this.focus.ci]?.id ?? null))
                return;
        }
        // 행 단위 편집 중 다른 행으로: leave 정책 (저장·확인·버림). 실패·확인 대기면 머묾
        if (this.rowEdit && nr !== this.rowEdit.row && !this.leaveRowEdit(vi, ci, extend))
            return;
        if (this.focusGuards.size && nr >= 0 && pr >= 0 && nr !== pr) {
            for (const g of this.focusGuards)
                if (g({ row: nr, prevRow: pr, grid: this }) === false)
                    return;
        }
        if (nr !== this.disp[this.focus.vi])
            this.escArm = -1;
        this.focus = { vi, ci };
        // 편집 중이 아니면 숨은 입력칸에 남은 글자를 비움 (다른 칸으로 가서 바로 입력할 때 섞이지 않게)
        if (!this.editing && !this.composing && this.ime.value)
            this.ime.value = '';
        if (!extend) {
            this.anchor = { vi, ci };
            if (!this.keepExtra && this.extraRanges.length) {
                this.extraRanges = [];
                this.ver++;
            }
        }
        this.announce();
        if (this.revealed.size) {
            const k = this.disp[vi] + ':' + this.cols[ci]?.id;
            for (const rk of [...this.revealed.keys()])
                if (rk !== k)
                    this.remask(rk);
        }
        this.hideTip();
        this.ensureVisible(vi, ci);
        this.bump();
        this.emitRowFocus();
        const m = this.getCellMessage(this.getFocusedRow(), this.cols[ci]?.id);
        if (m)
            this.showTip(m.message, m.level);
    }
    /** @internal */ emitRowFocus() {
        const row = this.getFocusedRow();
        if (row !== this.lastFocusRow) {
            const prev = this.lastFocusRow;
            this.lastFocusRow = row;
            // 빈 새 행 자동 취소 (CancelOnExit): 떠난 행이 추가 후 손대지 않은 행이면 버림 (INSERT 안 보냄)
            if (prev >= 0 && this.freshRows.has(prev) && !this.editing && this.discardIfUntouched(prev))
                return;
            const e = { row, prevRow: prev, grid: this };
            this.opts.onRowFocus?.(e);
            for (const f of this.focusListeners)
                f(e);
        }
    }
    /** 행이 추가 후 그대로면 버리고 true. 포커스는 원래 가려던 행으로 */
    /** @internal */ discardIfUntouched(row) {
        const init = this.freshRows.get(row);
        if (!init)
            return false;
        if (this.ds.rowState(row) !== RowState.INSERT) {
            this.freshRows.delete(row);
            return false;
        }
        if (!isUntouchedRow(this.ds.getRow(row), init)) {
            this.freshRows.delete(row);
            return false;
        }
        this.freshRows.delete(row);
        const target = this.getFocusedRow(), ci = this.focus.ci;
        this.dropUndoOfRow(row);
        this.ds.deleteRow(row); // INSERT → DISCARDED (보기에서 빠짐)
        if (target >= 0) {
            const vi = this.disp.indexOf(target);
            if (vi >= 0) {
                this.focus = { vi, ci };
                this.anchor = { ...this.focus };
            }
        }
        this.lastFocusRow = -2; // 아래에서 다시 알림
        this.emitRowFocus();
        this.bump();
        return true;
    }
    /** 그 행만 다룬 실행 취소 기록 버리기 (버린 새 행을 되살리지 않게) */
    /** @internal */ dropUndoOfRow(row) {
        const only = (x) => x.rows.every(y => y.row === row) && x.cells.every(y => y.row === row) && (x.rows.length + x.cells.length > 0);
        this.undoStack = this.undoStack.filter(x => !only(x));
        this.redoStack = this.redoStack.filter(x => !only(x));
    }
    /** 저장 전 정리: 새 항목 행은 고친 게 있으면 목록에 붙이고 아니면 버림, 손대지 않은 빈 새 행(cancelOnExit)은 버림 */
    /** @internal */ flushNewRows() {
        if (this.nirRow >= 0) {
            if (this.nirDirty())
                this.postNewItemRow(false, false);
            else
                this.cancelNewItemRow();
        }
        if (this.freshRows.size)
            for (const r of [...this.freshRows.keys()]) {
                const init = this.freshRows.get(r);
                this.freshRows.delete(r);
                if (this.ds.rowState(r) === RowState.INSERT && isUntouchedRow(this.ds.getRow(r), init)) {
                    this.dropUndoOfRow(r);
                    this.ds.deleteRow(r);
                }
            }
    }
    /** 선택한 셀 값 요약 (숫자만 합계·평균·최소·최대, 빈 칸 제외 개수). 여러 범위 선택이면 전부 */
    getSelectionSummary() {
        const vals = [];
        for (const r of this.selRanges())
            for (let vi = r.r0; vi <= r.r1; vi++) {
                const row = this.disp[vi];
                if (row == null || row < 0)
                    continue;
                for (let ci = r.c0; ci <= r.c1; ci++) {
                    const c = this.cols[ci];
                    if (!c || c.special)
                        continue;
                    vals.push(c.def.value ? c.def.value(row, this.ds) : this.ds.get(row, c.id));
                }
            }
        return summarizeValues(vals);
    }
    /** 선택 범위들 (기본: 현재 범위 하나) */
    /** @internal */ selRanges() { return [...this.extraRanges, this.range()]; }
    /** @internal */ inExtra(vi, ci) { for (const r of this.extraRanges)
        if (vi >= r.r0 && vi <= r.r1 && ci >= r.c0 && ci <= r.c1)
            return true; return false; }
    /** 여러 범위 선택 (Ctrl+끌기와 같음). 범위는 화면 행 번호(vi)·컬럼 번호 */
    getSelectedRanges() { return this.selRanges().map(r => ({ ...r, cols: this.cols.slice(r.c0, r.c1 + 1).filter(c => !c.special).map(c => c.id), rows: Array.from(this.disp.slice(r.r0, r.r1 + 1)).filter(x => x >= 0) })); }
    /** @internal */ updateSelSum() {
        const el = this.root.querySelector(':scope > .og-selsum');
        if (!el)
            return;
        const rs = this.selRanges();
        const cells = rs.reduce((n, r) => n + (r.r1 - r.r0 + 1) * (r.c1 - r.c0 + 1), 0);
        if (cells < 2) {
            if (!el.hidden)
                el.hidden = true;
            this.selSumKey = '';
            return;
        }
        const key = JSON.stringify(rs) + ':' + this.ds.version + ':' + this.disp.length;
        if (key === this.selSumKey)
            return;
        this.selSumKey = key;
        const s = this.getSelectionSummary(), want = Array.isArray(this.opts.selectionSummary) ? this.opts.selectionSummary : ['count', 'sum', 'avg', 'min', 'max'];
        const f = (v) => (v == null ? '-' : formatNumber(Math.round(v * 1000) / 1000, Math.abs(v) < 1000 && v % 1 ? 2 : 0));
        const parts = [];
        for (const k of want) {
            if (k === 'count')
                parts.push(`개수 ${s.count.toLocaleString('ko-KR')}`);
            else if (s.numCount)
                parts.push(`${this.t(AGG_LABEL[k])} ${f(s[k])}`);
        }
        el.textContent = parts.join('   ');
        el.hidden = false;
    }
    /** @internal */ ensureVisible(vi, ci) {
        const main = this.bodyPanes[1], rh = vi === NIR ? this.rh : this.rowH(vi), vh = main.clientHeight;
        const total = this.totalH();
        if (vi === NIR) { /* 새 항목 행은 고정 — 가로만 */ }
        else if (total <= MAX_SCROLL_PX) {
            const top = this.rowTop(vi), sg = this.opts.stickyGroupRows ? this.stickyChain(vi).length * rh : 0;
            if (top - sg < main.scrollTop)
                main.scrollTop = top - sg;
            else if (top + rh > main.scrollTop + vh)
                main.scrollTop = top + rh - vh;
        }
        else {
            const vTop = (main.scrollTop / Math.max(1, MAX_SCROLL_PX - vh)) * (total - vh);
            const top = this.rowTop(vi);
            if (top < vTop || top + rh > vTop + vh)
                main.scrollTop = (Math.max(0, top - vh / 2) / (total - vh)) * (MAX_SCROLL_PX - vh);
        }
        const c = this.cols[ci];
        if (c && c.pane === 1) {
            if (c.left < main.scrollLeft)
                main.scrollLeft = c.left;
            else if (c.left + c.width > main.scrollLeft + main.clientWidth)
                main.scrollLeft = c.left + c.width - main.clientWidth;
        }
    }
    /** 보기 위치 vi0~vi1 의 행 전체 선택 (포커스는 vi1 행 첫 데이터 컬럼) */
    /** @internal */ selectRowRange(vi0, vi1) {
        const last = this.cols.length - 1, first = this.firstDataCol();
        this.setFocus(vi0, last, false);
        this.setFocus(vi1, first, true);
    }
    /** 물리 행 선택 (행 전체 범위) */
    selectRow(row) { const vi = this.disp.indexOf(row); if (vi >= 0)
        this.selectRowRange(vi, vi); }
    /** @internal */ allChecked() { return this.view.length > 0 && this.view.every(r => this.checked.has(r)); }
    /** @internal */ targetRows() {
        const ch = this.getCheckedRows();
        if (ch.length)
            return ch;
        const { r0, r1 } = this.range();
        const out = [];
        for (let vi = r0; vi <= r1; vi++) {
            const r = this.disp[vi];
            if (r >= 0)
                out.push(r);
        }
        return out;
    }
    /** 다음/이전 편집 가능 셀 (행을 넘어감) */
    /** @internal */ nextEditable(vi, ci, dir) {
        const nC = this.cols.length;
        let v = vi, c = ci;
        const o = this.navOrd;
        for (let guard = 0; guard < nC * 3 + 2; guard++) {
            c = this.stepCol(c, dir);
            if (c >= nC) {
                c = o ? o[0] : 0;
                v++;
            }
            else if (c < 0) {
                c = o ? o[nC - 1] : nC - 1;
                v--;
            }
            if (v < 0 || v >= this.disp.length)
                return null;
            const row = this.disp[v];
            if (row >= 0 && this.canEditCol(this.cols[c], row))
                return { vi: v, ci: c };
        }
        return null;
    }
}
